<template>
  <form @submit.prevent="$emit('submit', data)">
    <div class="row">
      <div class="col-xs-6">
        <div class="help-text">
          <h3>
            General Information
          </h3>

          <p>
            In this panel, you can modify the core data of the job.
          </p>
        </div>
      </div>

      <div class="col-xs-18">
        <div class="panel panel-default">
          <div class="panel-body">
            <TextFormGroup
                v-model="data.title"
                name="title"
                label="Title"
                help-text="The title of the job."
                :error="(errors.title || [])[0]"
            />

            <TextareaFormGroup
                v-model="data.subheading"
                name="subheading"
                label="Subheading"
                help-text="The subheading of the job."
                :error="(errors.subheading || [])[0]"
            />
          </div>
        </div>
      </div>
    </div>

    <hr class="panel-separator">

    <div class="row">
      <div class="col-xs-6">
        <div class="help-text">
          <h3>
            Job Details
          </h3>

          <p>
            In this panel, you can enter the specific details about the job: position, address, contact information and
            the timeline of the process.
          </p>
        </div>
      </div>

      <div class="col-xs-18">
        <div class="panel panel-default">
          <div class="panel-body">
            <TextFormGroup
                v-model="data.position"
                name="position"
                label="Position"
                help-text="The position of the job."
                :error="(errors.position || [])[0]"
            />

            <TextFormGroup
                v-model="data.employment_form"
                name="employment_form"
                label="Employment Form"
                help-text="The employment form of the job."
                :error="(errors.employment_form || [])[0]"
            />

            <TextFormGroup
                v-model="data.work_address"
                name="work_address"
                label="Work Address"
                help-text="The work address of the job."
                :error="(errors.work_address || [])[0]"
            />

            <TextFormGroup
                v-model="data.email"
                name="email"
                label="Contact Email"
                help-text="The email of the job."
                :error="(errors.email || [])[0]"
            />

            <TextFormGroup
                v-model="data.phone"
                name="phone"
                label="Contact Phone"
                help-text="The phone of the job."
                :error="(errors.phone || [])[0]"
            />

            <TextFormGroup
                v-model="data.link"
                name="link"
                label="Application URL"
                help-text="The link of the job."
                :error="(errors.link || [])[0]"
            />

            <DateFormGroup
                v-model="data.application_deadline_at"
                name="application_deadline_at"
                label="Application Deadline At"
                help-text="The deadline for applications."
                :error="(errors.application_deadline_at || [])[0]"
            />

            <DateFormGroup
                v-model="data.employment_starts_at"
                name="employment_starts_at"
                label="Employment Starts At"
                help-text="The date that the employment starts."
                :error="(errors.employment_starts_at || [])[0]"
            />

            <SelectFormGroup
                v-model="data.content_job_region_id"
                name="content_job_region_id"
                label="Region"
                :error="(errors.content_job_region_id || [])[0]"
                :options="contentJobRegionOptions"
                :nullable="true"
            />

            <RadioFormGroup
                v-model="data.is_featured"
                name="is_featured"
                label="Featured"
                :error="(errors.is_featured || [])[0]"
                :options="[{value: true, label: 'Yes'}, {value: false, label: 'No'}]"
            />
          </div>
        </div>
      </div>
    </div>

    <hr class="panel-separator">

    <div class="row">
      <div class="col-xs-6">
        <div class="help-text">
          <h3>
            Body
          </h3>

          <p>
            These panels contain the main text of the job posting. You can add more blocks to the main text by using the
            button below.
          </p>

          <div class="btn-group" style="margin-top: 4px">
            <button type="button" class="btn btn-black btn-sm dropdown-toggle" data-toggle="dropdown">
              <i class="fa fa-plus"></i> Add <span class="caret"></span>
            </button>

            <ul class="dropdown-menu">
              <li v-for="bodyCategoryOption in bodyCategoryOptions">
                <a href="#"
                   @click.prevent="data.content_bodies.push({body_category: bodyCategoryOption.value, body_category_parameters: {}})"
                >
                  {{ bodyCategoryOption.label }}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <Draggable
          element="div"
          v-model="data.content_bodies"
          handle=".handle"
          chosen-class="interval--dragging"
          class="col-xs-18"
      >
        <ContentBodyFormPanel
            v-for="(contentBody, index) in data.content_bodies"
            v-model="data.content_bodies[index]"
            :key="index"
            :errors="getBodyCategoryParametersErrors(index)"
            :initial-collapsed="true"
            :company-id="companyId"
            @remove="data.content_bodies.splice(index, 1)"
        />
      </Draggable>
    </div>

    <hr class="panel-separator">

    <div class="row">
      <div class="col-xs-6">
        <div class="help-text">
          <h3>
            Publication
          </h3>

          <p>
            In this panel, you can adjust the publication settings for the job.
          </p>
        </div>
      </div>

      <div class="col-xs-18">
        <div class="panel panel-default">
          <div class="panel-body">
            <DateFormGroup
                v-model="data.publish_at"
                name="publish_at"
                label="Publish At"
                help-text="The date that the job should be published."
                :error="(errors.publish_at || [])[0]"
                format="YYYY-MM-DD HH:mm:ss"
                original-format="YYYY-MM-DD HH:mm:ss"
            />

            <DateFormGroup
                v-model="data.unpublish_at"
                name="unpublish_at"
                label="Unpublish At"
                help-text="The date that the job should be unpublished."
                :error="(errors.unpublish_at || [])[0]"
                format="YYYY-MM-DD HH:mm:ss"
                original-format="YYYY-MM-DD HH:mm:ss"
            />

            <StaticFormGroup
                v-if="edit"
                label="Author"
                :value="author"
            />
          </div>
        </div>
      </div>
    </div>

    <hr class="panel-separator">

    <div class="row">
      <div class="col-xs-6">
        <div class="help-text">
          <h3>
            Advanced Setup
          </h3>

          <p>
            These settings control the advanced setup for the job.
          </p>
        </div>
      </div>

      <div class="col-xs-18">
        <div class="panel panel-default">
          <div class="panel-body">
            <StaticFormGroup
                v-if="edit"
                :value="data.path"
                label="Path"
                help-text="The path of the job."
            />
          </div>
        </div>
      </div>
    </div>

    <hr class="panel-separator">

    <div class="row">
      <div class="col-xs-offset-6 col-xs-18">

        <button type="submit" class="btn btn-black pull-left" style="margin-bottom: 22px">
          <i class="fa fa-save"></i> Save
        </button>

        <a
            v-if="url"
            :href="url + '?preview=true'"
            class="btn btn-white pull-right"
            target="_blank"
            style="border: 1px solid #000000;"
        >
          <i class="fas fa-external-link-alt"></i> Preview
        </a>

        <div class="clearfix"></div>
      </div>
    </div>
  </form>
</template>

<script>
import store from '@/store'
import BodyCategoryParameterForm from "../../ContentBodies/BodyCategoryParameterForms/Base.vue";
import ContentBodyFormPanel from "../../ContentBodies/Panels/Form.vue";
import DateFormGroup from '@/vendor/forms/FormGroups/Block/Date'
import Draggable from 'vuedraggable'
import SubmitFormGroup from '@/vendor/forms/FormGroups/Block/Submit'
import TextareaFormGroup from '@/vendor/forms/FormGroups/Block/Textarea'
import TextFormGroup from '@/vendor/forms/FormGroups/Block/Text'
import RepeatableFormGroup from "../../../Forms/RepeatableFormGroup.vue";
import SelectFormGroup from "../../../../vendor/forms/FormGroups/Block/Select.vue";
import RadioFormGroup from "../../../../vendor/forms/FormGroups/Block/Radio.vue";
import StaticFormGroup from "../../../../vendor/forms/FormGroups/Block/Static.vue";

export default {
  components: {
    Draggable,
    SelectFormGroup,
    RadioFormGroup,
    RepeatableFormGroup,
    DateFormGroup,
    SubmitFormGroup,
    TextareaFormGroup,
    TextFormGroup,
    BodyCategoryParameterForm,
    ContentBodyFormPanel,
    StaticFormGroup
  },

  props: {
    initial: {
      required: false,
      type: Object,
      default: () => ({})
    },

    errors: {
      required: true,
      type: Object
    },

    contentJobRegions: {
      required: true,
      type: Array
    },

    edit: {
      required: false,
      default: false
    },

    url: {
      required: false,
      type: String
    },

    companyId: {
      required: true,
      type: [String, Number]
    },
  },

  data() {
    return {
      data: {...this.initial},

      shared: store
    }
  },

  methods: {
    getBodyCategoryParametersErrors(index) {
      return Object.entries(this.errors)
          .filter(entry => entry[0].startsWith('content_bodies.' + index + '.body_category_parameters.'))
          .reduce((result, entry) => ({
            ...result,
            [entry[0].substring(('content_bodies.' + index + '.body_category_parameters.').length)]: entry[1]
          }), {})
    }
  },

  computed: {
    bodyCategoryOptions() {
      return [
        {value: 'text', label: 'Text'},
        {value: 'fact-box', label: 'Fact Box'},
        {value: 'quote', label: 'Quote'},
        {value: 'image', label: 'Image'},
        {value: 'gallery', label: 'Gallery'},
        {value: 'podcast', label: 'Podcast'},
        {value: 'social-media', label: 'Social Media'},
        {value: 'video', label: 'Video'},
      ]
    },

    contentJobRegionOptions() {
      return this.contentJobRegions.map(contentJobRegion => ({
        value: contentJobRegion.id,
        label: contentJobRegion.attributes.name
      }))
    },

    author() {
      return [
        this.data.author_name,
        this.data.author_email,
      ].join('&nbsp;&middot;&nbsp;')
    }
  }
}
</script>